import './BigDevicePage.scss';
import { BigDeviceList } from '../../components/BigDeviceList/BigDeviceList';
import { Cart } from '../../components/Cart';
import { ProductsPopup } from '../../components/ProductsPopup';
import { useState, useEffect } from 'react';
import { selectProductForDisplay, selectImageForDisplay, setDisplayOrientation } from '../../services/bigDisplayData';
import { useProducts } from '../../contexts/ProductsContext';
import { useDisplays } from '../../contexts/DisplaysContext';
import { MediaLibraryPopup } from '../../components/MediaLibraryPopup/MediaLibraryPopup';

export const BigDevicePage = () => {
  const [showProductsPopup, setShowProductsPopup] = useState<boolean>(false);
  const [showMediaLibraryPopup, setShowMediaLibraryPopup] = useState<boolean>(false);
  const [selectedDisplayId, setSelectedDisplayId] = useState<number | null>(null);
  const [isUploadingMedia, setIsUploadingMedia] = useState<boolean>(false);

  const { products, getAuthenticatedImage } = useProducts();
  const { displays: bigDisplays, loading, error, refreshDisplays, updateDisplay } = useDisplays();

  // Trigger refetch when component mounts to ensure data is loaded after navigation
  useEffect(() => {
    const storeId = localStorage.getItem('storeId');
    if (storeId && (!bigDisplays || bigDisplays.length === 0) && !loading) {
      console.log('BigDevicePage mounted with storeId, triggering refetch');
      refreshDisplays();
    }
  }, []);

  const handleOpenProductsPopup = (displayId: number) => {
    setSelectedDisplayId(displayId);
    setShowProductsPopup(!showProductsPopup);
  };

  const handleOpenMediaLibraryPopup = () => {
    setShowMediaLibraryPopup(true);
  };

  const handleUploadClick = (displayId: number) => {
    setSelectedDisplayId(displayId);
    setShowMediaLibraryPopup(true);
  };

  const handleMediaSelect = async (mediaUrl: string) => {
    if (!selectedDisplayId) {
      alert('No display selected');
      return;
    }

    setIsUploadingMedia(true);
    
    try {
      const response = await selectImageForDisplay(selectedDisplayId, [mediaUrl]);
      console.log('Media uploaded successfully:', response);
      // Update the display with new image using context
      updateDisplay(selectedDisplayId, { current_image: [mediaUrl] });
    } catch (error) {
      console.error('Error uploading media to display:', error);
      alert('Failed to upload image to display. Please try again.');
    } finally {
      setIsUploadingMedia(false);
      setShowMediaLibraryPopup(false);
    }
  };

  const handleProductSelect = async (productId: number) => {
    if (!selectedDisplayId) return;

    try {
      const response = await selectProductForDisplay(selectedDisplayId, productId);
      console.log('Product selected successfully:', response);
      
      // Update the display with new images using context
      updateDisplay(selectedDisplayId, { current_image: response.image_urls });
      
    } catch (error) {
      console.error('Error selecting product for display:', error);
      throw error;
    }
  };

  const handleOrientationChange = async (displayId: number, orientation: 'portrait' | 'landscape') => {
    try {
      const response = await setDisplayOrientation(displayId, orientation);
      console.log('Orientation changed successfully:', response);
      // Update the display with new orientation using context
      updateDisplay(displayId, { orientation });
    } catch (error) {
      console.error('Error changing display orientation:', error);
      alert('Failed to change display orientation. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="devicePage">
        <div>Loading displays...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="devicePage">
        <div>Error: {error}</div>
        <button onClick={refreshDisplays}>Retry</button>
      </div>
    );
  }

  return (
    <div className="devicePage">
      <Cart/>
      <div className="devicePage__content">
        <div className="devicePage__header">
          <p className="devicePage__header-title">Display</p>
          {/* <button 
            className='devicePage__header-button'
            onClick={handleOpenMediaLibraryPopup}
          >
            <img className="devicePage__header-icon" src={sparklesIcon} alt="Sparkles Icon" />
            Media Library
          </button> */}
        </div>

        <BigDeviceList 
          displays={bigDisplays} 
          onImageIconClick={handleOpenProductsPopup}
          onUploadClick={handleUploadClick}
          onOrientationChange={handleOrientationChange}
        />
      </div>

      {showProductsPopup && (
        <div className="devicePage__popup-overlay">
          <ProductsPopup
            products={products}
            onClose={() => setShowProductsPopup(false)}
            onGetAuthenticatedImage={getAuthenticatedImage}
            onProductSelect={handleProductSelect}
          />
        </div>
      )}

      {showMediaLibraryPopup && (
        <div className="devicePage__popup-overlay">
          <MediaLibraryPopup 
            onClose={() => setShowMediaLibraryPopup(false)}
            onMediaSelect={handleMediaSelect}
            isUploading={isUploadingMedia}
          />
        </div>
      )}
    </div>
  );
}; 